import type {Workload} from "../../3-workload-analysis/model/Workload.ts";
import type {ScoreResult} from "../model/ScoreResult.ts";

// Thresholds for determining high concurrency
const HIGH_CONCURRENCY_THRESHOLD = 3 // Mean overlap of 3+ concurrent tasks
const MODERATE_CONCURRENCY_THRESHOLD = 2 // Mean overlap of 2+ concurrent tasks

const HIGH_CONCURRENCY_SCORE = 3
const MODERATE_CONCURRENCY_SCORE = 1.5

/**
 * Scores workloads based on their concurrency patterns.
 * Workloads with higher concurrency benefit more from DPC's superior concurrency support.
 */
export function scorerConcurrency(workload: Workload): ScoreResult<Workload> | undefined {
    if (workload.tasks.length === 0) {
        return undefined
    }

    // Calculate average mean overlap across all task batches
    let totalMeanOverlap = 0
    let taskCount = 0

    workload.tasks.forEach(task => {
        if (task.overlaps) {
            totalMeanOverlap += task.overlaps.meanOverlap
            taskCount++
        }
    })

    if (taskCount === 0) {
        return undefined
    }

    const avgMeanOverlap = totalMeanOverlap / taskCount

    // Determine if this workload has significant concurrency
    if (avgMeanOverlap >= HIGH_CONCURRENCY_THRESHOLD) {
        return {
            scorerId: "CONCURRENCY_UPWEIGHT_HIGH",
            scoredObject: workload,
            score: HIGH_CONCURRENCY_SCORE,
            description: [
                `This workload runs with high concurrency (average ${avgMeanOverlap.toFixed(1)} concurrent tasks). ` +
                `DPC's enhanced concurrency support will provide better performance and reliability compared to Matillion ETL.`
            ]
        }
    } else if (avgMeanOverlap >= MODERATE_CONCURRENCY_THRESHOLD) {
        return {
            scorerId: "CONCURRENCY_UPWEIGHT_MODERATE",
            scoredObject: workload,
            score: MODERATE_CONCURRENCY_SCORE,
            description: [
                `This workload uses moderate concurrency (average ${avgMeanOverlap.toFixed(1)} concurrent tasks). ` +
                `DPC handles concurrent execution more efficiently than Matillion ETL.`
            ]
        }
    }

    return undefined
}
